/**
 * Maps the per-field AI enrichment result (from the `enrichElectronicModule`
 * callable — `{ displayType: { value, confidence, reasoning }, … }`) onto the
 * editable module form fields. Defensive per-type coercion so a value the model
 * returns still applies whether it arrives as a string, number, or array.
 *
 * `manufacturer` is intentionally excluded: it would arrive as a free-text maker
 * name, but the form needs a `manufacturerId` FK, so the user resolves it via
 * the manufacturer picker rather than us guessing an id.
 */
import type { ModuleEnrichResult } from '../../services/firebase';

/** Module fields the AI can propose, normalised for the form (numbers → text). */
export interface ModuleAiFields {
  name?: string;
  aiDescription?: string;
  displayType?: string;
  lcdType?: string;
  battery?: string;
  batteryLifeMonths?: string;
  chargeTimeHours?: string;
  lightType?: string;
  displayColor?: string;
  textColor?: string;
  generation?: string;
  sensors?: string[];
  displayLayout?: string;
  viewingAngle?: string;
  dstSupport?: string;
  calendarType?: string;
  stopwatchPrecision?: string;
  powerSource?: string;
  syncSource?: string;
  timeSystems?: string[];
  alarmTypes?: string[];
  alsoKnownAs?: string[];
  source?: string;
  sourceUrl?: string;
}

const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;

const numStr = (v: unknown): string | undefined => {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'string' && v.trim() && !Number.isNaN(Number(v)))
    return String(Number(v));
  return undefined;
};

const arr = (v: unknown): string[] | undefined => {
  if (typeof v === 'string' && v.trim()) return [v.trim()];
  if (!Array.isArray(v)) return undefined;
  const a = v
    .filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    .map((x) => x.trim());
  return a.length ? a : undefined;
};

/** Unwrap `.value` from a per-field result entry, if present. */
function fieldValue(result: ModuleEnrichResult, key: string): unknown {
  const entry = result[key];
  return entry && typeof entry === 'object' ? entry.value : undefined;
}

/**
 * Unwrap a multi-value field entry. The callable returns `sensors` /
 * `timeSystems` / `alarmTypes` under `values` (plural), so prefer that; fall
 * back to `value` for robustness against a scalar-shaped entry.
 */
function fieldValues(result: ModuleEnrichResult, key: string): unknown {
  const entry = result[key];
  if (!entry || typeof entry !== 'object') return undefined;
  return entry.values ?? entry.value;
}

/**
 * Coerce the callable's per-field result set into normalised form fields. Only
 * keys the model actually returned are populated; the rest stay undefined so the
 * apply-review diff skips them.
 */
export function moduleResultToFields(
  result: ModuleEnrichResult,
): ModuleAiFields {
  const v = (key: string) => fieldValue(result, key);
  const vs = (key: string) => fieldValues(result, key);
  return {
    name: str(v('name')),
    aiDescription: str(v('aiDescription')) ?? str(v('description')),
    displayType: str(v('displayType')),
    lcdType: str(v('lcdType')),
    battery: str(v('battery')),
    batteryLifeMonths: numStr(v('batteryLifeMonths')),
    chargeTimeHours: numStr(v('chargeTimeHours')),
    lightType: str(v('lightType')),
    displayColor: str(v('displayColor')),
    textColor: str(v('textColor')),
    generation: str(v('generation')),
    sensors: arr(vs('sensors')),
    displayLayout: str(v('displayLayout')),
    viewingAngle: str(v('viewingAngle')),
    dstSupport: str(v('dstSupport')),
    calendarType: str(v('calendarType')),
    stopwatchPrecision: str(v('stopwatchPrecision')),
    powerSource: str(v('powerSource')),
    syncSource: str(v('syncSource')),
    timeSystems: arr(vs('timeSystems')),
    alarmTypes: arr(vs('alarmTypes')),
    alsoKnownAs: arr(vs('alsoKnownAs')),
    source: str(v('source')),
    sourceUrl: str(v('sourceUrl')),
  };
}
